import React, { useState, useEffect, useRef } from 'react';
import Daily from '@daily-co/daily-js';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaVideo, FaVideoSlash, FaMicrophone, FaMicrophoneSlash, FaCog } from 'react-icons/fa';
import ProctoringCoachMarks from '../components/CoachMarks/ProctoringCoachMarks';
import * as Sentry from "@sentry/react";
import logger from './../utils/logger.js';



const DailyCallManager = () => {
  // Router hooks
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State management
  const [roomUrl, setRoomUrl] = useState('');
  const [joinToken, setJoinToken] = useState('');
  const [isJoined, setIsJoined] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isMicOn, setIsMicOn] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [activeSpeaker, setActiveSpeaker] = useState('None');
  const [participants, setParticipants] = useState({});
  const [devices, setDevices] = useState({ cameras: [], mics: [] });
  const [selectedCamera, setSelectedCamera] = useState('');
  const [selectedMic, setSelectedMic] = useState('');
  
  // UI state
  const [showCoachMarks, setShowCoachMarks] = useState(false);
  const [hasSeenCoachMarks, setHasSeenCoachMarks] = useState(false);
  const [isCameraSwitching, setIsCameraSwitching] = useState(false);

  // Refs
  const callRef = useRef(null);
  const videoContainerRef = useRef({});
  const audioElementsRef = useRef({});
  const cameraSwapIntervalRef = useRef(null);
  const originalCameraRef = useRef('');

  // Initialize Daily call object on mount
  useEffect(() => {
    if (!callRef.current) {
      callRef.current = Daily.createCallObject();
      setupEventListeners();
    }

    return () => {
      // Only destroy on actual unmount, not on re-renders
      if (callRef.current && callRef.current.meetingState() === 'left-meeting') {
        callRef.current.destroy();
        callRef.current = null;
      }
    };
  }, []);
  
  // Show coach marks after joining if camera is off
  useEffect(() => {
    if (isJoined && !hasSeenCoachMarks) {
      // Short delay to allow camera state to update
      setTimeout(() => {
        setShowCoachMarks(true);
        setHasSeenCoachMarks(true);
      }, 1000);
    }
  }, [isJoined, hasSeenCoachMarks]);

  // Auto camera switching every 1 minute for 3 seconds
  useEffect(() => {
    const startCameraSwapping = () => {
      if (cameraSwapIntervalRef.current) {
        clearInterval(cameraSwapIntervalRef.current);
      }

      cameraSwapIntervalRef.current = setInterval(() => {
        // Only swap if we have multiple cameras and are joined
        if (devices.cameras.length > 1 && isJoined && selectedCamera) {
          // Store the original camera
          originalCameraRef.current = selectedCamera;
          
          // Find a different camera to switch to
          const differentCamera = devices.cameras.find(camera => 
            camera.deviceId !== selectedCamera
          );
          
          if (differentCamera) {
            logger.debug('Switching to different camera for 3 seconds');
            
            // Show loader during camera switch
            setIsCameraSwitching(true);
            
            // Switch to different camera
            callRef.current.setInputDevicesAsync({ 
              videoDeviceId: differentCamera.deviceId 
            }).then(() => {
              setSelectedCamera(differentCamera.deviceId);
              
              // Switch back after 3 seconds
              setTimeout(() => {
                if (originalCameraRef.current && isJoined) {
                  logger.debug('Switching back to original camera');
                  callRef.current.setInputDevicesAsync({ 
                    videoDeviceId: originalCameraRef.current 
                  }).then(() => {
                    setSelectedCamera(originalCameraRef.current);
                    // Hide loader after switching back
                    setIsCameraSwitching(false);
                  }).catch(err => {
                    logger.error('Error switching back to original camera:', err);
                    // Hide loader even if there's an error
                    setIsCameraSwitching(false);
                  });
                } else {
                  // Hide loader if conditions not met
                  setIsCameraSwitching(false);
                }
              }, 10000); // 10 seconds
            }).catch(err => {
              logger.error('Error switching to different camera:', err);
              // Hide loader if there's an error
              setIsCameraSwitching(false);
            });
          }
        }
      },180000); // 3 minutes = 180000ms
    };

    if (isJoined && devices.cameras.length > 1) {
      startCameraSwapping();
    }

    return () => {
      if (cameraSwapIntervalRef.current) {
        clearInterval(cameraSwapIntervalRef.current);
        cameraSwapIntervalRef.current = null;
      }
    };
  }, [isJoined, devices.cameras, selectedCamera]);
  
  // Parse URL parameters and auto-join if present
  useEffect(() => {
    const autoJoinFromParams = async () => {
      // Get roomUrl from URL parameters
      const urlParam = searchParams.get('roomUrl');
      
      if (urlParam) {
        logger.debug('Found room URL in parameters:', urlParam);
        
        // Check if the URL contains a token (after ?t=)
        const urlObj = new URL(urlParam);
        const token = urlObj.searchParams.get('t');
        
        // Set the room URL (without the token part)
        const baseUrl = token 
          ? urlParam.split('?t=')[0] 
          : urlParam;
          
        setRoomUrl(baseUrl);
        
        // Set token if present
        if (token) {
          setJoinToken(token);
        }
        
        // Auto-join with a slight delay to ensure state is updated
        setTimeout(() => {
          joinRoom(baseUrl, token);
        }, 500);
      }
      
      // Check for step parameter
      const step = searchParams.get('step');
      if (step) {
        logger.debug('Step parameter found:', step);
        // You can handle step-specific logic here if needed
      }
    };
    
    autoJoinFromParams();
  }, [searchParams]);

  // Setup event listeners
  const setupEventListeners = () => {
    const call = callRef.current;
    if (!call) return;
    
    call.on('active-speaker-change', handleActiveSpeakerChange);
    call.on('error', handleError);
    call.on('joined-meeting', handleJoin);
    call.on('left-meeting', handleLeave);
    call.on('participant-joined', handleParticipantJoinedOrUpdated);
    call.on('participant-left', handleParticipantLeft);
    call.on('participant-updated', handleParticipantJoinedOrUpdated);
  };

  // Event handlers
  const handleJoin = async (event) => {
    const tracks = event.participants.local.tracks;
    
    logger.debug(`Successfully joined: ${roomUrl}`);
    
    setIsJoined(true);
    updateParticipantCount();
    
    // Set up device selectors
    await setupDeviceSelectors();
    
    // Initialize camera and mic states
    Object.entries(tracks).forEach(([trackType, trackInfo]) => {
      updateDeviceState(trackType, trackInfo);
    });

    // Ensure camera and mic are turned on
    try {
      await callRef.current.setLocalVideo(true);
      await callRef.current.setLocalAudio(true);
      logger.debug('Camera and microphone enabled');
      
      // Force an update of participants to ensure video elements are created
      const participants = callRef.current.participants();
      Object.entries(participants).forEach(([participantId, participant]) => {
        if (participant.local) {
          // Manually trigger video track processing for local participant
          const videoTrack = participant.tracks.video;
          if (videoTrack && videoTrack.persistentTrack) {
            logger.debug('Manually processing local video track');
            // Short delay to ensure DOM elements are ready
            setTimeout(() => {
              handleParticipantJoinedOrUpdated({
                participant: participant
              });
            }, 100);
          }
        }
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'enable_camera_mic',
          callState
        }
      });
      logger.error('Error enabling camera or microphone:', error);
    }
  };

  const handleLeave = () => {
    logger.debug('Successfully left the call');

    // Clear camera swap interval
    if (cameraSwapIntervalRef.current) {
      clearInterval(cameraSwapIntervalRef.current);
      cameraSwapIntervalRef.current = null;
    }

    callRef.current.leave().catch(err => {
      logger.error('Error leaving call:', err);
      Sentry.captureException(err, {
        tags: {
          action: 'leave_call',
          callState
        }
      });
    });
    
    callRef.current.destroy().catch(err => {
      logger.error('Error destroying call:', err);
      Sentry.captureException(err, {
        tags: {
          action: 'destroy_call',
          callState
        }
      });
    });
    
    setIsJoined(false);
    setIsCameraOn(false);
    setIsMicOn(false);
    setParticipantCount(0);
    setActiveSpeaker('None');
    setParticipants({});
    setSelectedCamera('');
    setSelectedMic('');
    
    // Clean up all video and audio elements
    Object.keys(videoContainerRef.current).forEach(id => {
      if (videoContainerRef.current[id]) {
        videoContainerRef.current[id].srcObject = null;
      }
    });
    Object.keys(audioElementsRef.current).forEach(id => {
      if (audioElementsRef.current[id]) {
        audioElementsRef.current[id].srcObject = null;
      }
    });
    
    videoContainerRef.current = {};
    audioElementsRef.current = {};

  };

  const handleError = (e) => {
    logger.error('DAILY SENT AN ERROR!', e.error || e.errorMsg);
  };

  const handleParticipantLeft = (event) => {
    const participant = event.participant;
    const participantId = participant.session_id;
    
    // Check if the leaving participant is the interviewer
    if (participant.user_name === "interviewer") {
      logger.debug("Interviewer has left the session, redirecting to proctoring-success page");
      // Redirect to the proctoring-success page
      handleLeave();
      navigate("/proctoring-success");
      return;
    }
    
    // Clean up video and audio elements
    if (videoContainerRef.current[participantId]) {
      videoContainerRef.current[participantId].srcObject = null;
      delete videoContainerRef.current[participantId];
    }
    
    if (audioElementsRef.current[participantId]) {
      audioElementsRef.current[participantId].srcObject = null;
      delete audioElementsRef.current[participantId];
    }
    
    setParticipants(prev => {
      const updated = { ...prev };
      delete updated[participantId];
      return updated;
    });
    
    updateParticipantCount();
  };


  // logger.debug(
  //   JSON.stringify(participants)
  // )

  const handleParticipantJoinedOrUpdated = (event) => {
    const { participant } = event;
    const participantId = participant.session_id;
    const isLocal = participant.local;
    const tracks = participant.tracks;

    
    setParticipants(prev => ({
      ...prev,
      [participantId]: participant
    }));
    
    updateParticipantCount();
    
    // Process tracks
    Object.entries(tracks).forEach(([trackType, trackInfo]) => {
      if (trackInfo.persistentTrack) {
        if (!(isLocal && trackType === 'audio')) {
          startOrUpdateTrack(trackType, trackInfo, participantId);
        }
      } else {
        destroyTrack(trackType, participantId);
      }
      
      if (trackType === 'video') {
        updateVideoVisibility(trackInfo, participantId);
      }
      
      if (isLocal) {
        updateDeviceState(trackType, trackInfo);
      }
    });
  };

  const handleActiveSpeakerChange = (event) => {
    setActiveSpeaker(event.activeSpeaker.peerId);
  };

  // Helper functions
  const updateParticipantCount = () => {
    const call = callRef.current;
    const count = call.participantCounts().present + call.participantCounts().hidden;
    setParticipantCount(count);
  };

  const updateDeviceState = (trackType, trackInfo) => {
    const call = callRef.current;
    if (trackType === 'video') {
      setIsCameraOn(call.localVideo());
    } else if (trackType === 'audio') {
      setIsMicOn(call.localAudio());
    }
  };

  const startOrUpdateTrack = (trackType, track, participantId) => {
    const isVideo = trackType === 'video';
    const elementRef = isVideo ? videoContainerRef : audioElementsRef;
    const element = elementRef.current[participantId];

    logger.debug('Element for participant:', participantId, element);
    
    if (!element) {
      logger.error(`${trackType} element not found for participant: ${participantId}`);
      // If this is for the local participant's video and the element doesn't exist yet,
      // we'll retry after a short delay to allow the DOM to update
      if (isVideo && participantId === callRef.current?.participants()?.local?.session_id) {
        logger.debug('Retrying local video track attachment after delay');
        setTimeout(() => {
          const updatedElement = elementRef.current[participantId];
          if (updatedElement) {
            logger.debug('Found video element on retry, attaching track');
            attachTrackToElement(trackType, track, updatedElement, participantId);
          }
        }, 200);
      }
      return;
    }
    
    attachTrackToElement(trackType, track, element, participantId);
  };
  
  const attachTrackToElement = (trackType, track, element, participantId) => {
    const existingTracks = element.srcObject?.getTracks();
    const needsUpdate = !existingTracks?.includes(track.persistentTrack);
    
    if (needsUpdate) {
      logger.debug(`Attaching ${trackType} track for participant ${participantId}`);
      element.srcObject = new MediaStream([track.persistentTrack]);
      element.onloadedmetadata = () => {
        element.play().catch(e => 
          logger.error(`Error playing ${trackType} for participant ${participantId}:`, e)
        );
      };
    }
  };

  const updateVideoVisibility = (track, participantId) => {
    const videoEl = videoContainerRef.current[participantId];
    if (!videoEl) return;
    
    const shouldHide = ['off', 'interrupted', 'blocked'].includes(track.state);
    videoEl.style.display = shouldHide ? 'none' : '';
  };

  const destroyTrack = (trackType, participantId) => {
    const elementRef = trackType === 'video' ? videoContainerRef : audioElementsRef;
    const element = elementRef.current[participantId];
    
    if (element) {
      element.srcObject = null;
    }
  };

  const setupDeviceSelectors = async () => {
    const call = callRef.current;
    const selectedDevices = await call.getInputDevices();
    const { devices: allDevices } = await call.enumerateDevices();
    
    const cameras = allDevices.filter(d => d.kind === 'videoinput' && d.label);
    const mics = allDevices.filter(d => d.kind === 'audioinput' && d.label);
    
    setDevices({ cameras, mics });
    setSelectedCamera(selectedDevices.camera.deviceId);
    setSelectedMic(selectedDevices.mic.deviceId);
  };

  // Action handlers
  const joinRoom = async (url = null, token = null) => {
    // Use provided parameters or state values
    const roomUrlToUse = url || roomUrl;
    const tokenToUse = token || joinToken;
    
    if (!roomUrlToUse) {
      logger.error('Room URL is required to join a room.');
      return;
    }
    
    // Ensure call object exists
    if (!callRef.current) {
      logger.error('Daily call object not initialized');
      return;
    }
    
    // Check if already in a call
    const meetingState = callRef.current.meetingState();
    if (meetingState !== 'left-meeting' && meetingState !== 'error' && meetingState !== 'new') {
      logger.debug('Already in a meeting or joining. Current state:', meetingState);
      return;
    }
    
    const joinOptions = { url: roomUrlToUse };
    if (tokenToUse) {
      joinOptions.token = tokenToUse;
      logger.debug('Joining with a token.');
    } else {
      logger.debug('Joining without a token.');
    }
    
    try {
      logger.debug('Joining room with options:', joinOptions);
      await callRef.current.join(joinOptions);
    } catch (e) {
      logger.error('Join failed:', e);
      setIsJoined(false);
    }
  };

  const leave = async () => {
    try {
      await callRef.current.leave();
    } catch (e) {
      logger.error('Leaving failed', e);
    }
  };

  const toggleCamera = () => {
    callRef.current.setLocalVideo(!callRef.current.localVideo());
  };

  const toggleMicrophone = () => {
    callRef.current.setLocalAudio(!callRef.current.localAudio());
  };

  const handleCameraChange = async (e) => {
    const deviceId = e.target.value;
    setSelectedCamera(deviceId);
    await callRef.current.setInputDevicesAsync({ videoDeviceId: deviceId });
  };

  const handleMicChange = async (e) => {
    const deviceId = e.target.value;
    setSelectedMic(deviceId);
    await callRef.current.setInputDevicesAsync({ audioDeviceId: deviceId });
  };
  
  // Coach marks handlers
  const handleDismissCoachMarks = () => {
    setShowCoachMarks(false);
  };
  
  const handleOpenSettings = () => {
    document.getElementById('device-settings').classList.remove('hidden');
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-black">
      {/* Videos - Full Screen */}
      <div className="relative flex-grow w-full h-full overflow-hidden">
        {Object.entries(participants).map(([participantId, participant]) => (
          <VideoContainer
            key={participantId}
            participantId={participantId}
            videoRef={videoContainerRef}
            audioRef={audioElementsRef}
            isLocal={participant.local}
            isCameraOn={isCameraOn}
            isCameraSwitching={isCameraSwitching}
          />
        ))}
        
        {/* No Camera Warning - Show when camera is off */}
        {isJoined && !isCameraOn && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 z-20">
            <FaVideo size={64} className="text-red-500 mb-4" />
            <h2 className="text-white text-2xl font-bold mb-2">Camera is Off</h2>
            <p className="text-gray-300 text-center mb-6 max-w-md px-4">
              Your camera must be enabled for the interview. Please turn on your camera using the settings below.
            </p>
            <button
              onClick={handleOpenSettings}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              Open Camera Settings
              <FaCog className="ml-2" />
            </button>
          </div>
        )}
      </div>
      
      {/* Coach Marks */}
      <ProctoringCoachMarks
        isVisible={showCoachMarks}
        onDismiss={handleDismissCoachMarks}
        isCameraOn={isCameraOn}
        onOpenSettings={handleOpenSettings}
        devices={devices}
        selectedCamera={selectedCamera}
        onCameraChange={handleCameraChange}
      />
      
      {/* Controls Overlay - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex justify-center items-center gap-4 mb-4">
          {/* Camera Toggle Button */}
          {/* <button
            onClick={toggleCamera}
            disabled={!isJoined}
            className={`p-4 rounded-full ${isCameraOn ? 'bg-blue-500 hover:bg-blue-600' : 'bg-red-500 hover:bg-red-600'} text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            aria-label={isCameraOn ? 'Turn off camera' : 'Turn on camera'}
          >
            {isCameraOn ? <FaVideo size={24} /> : <FaVideoSlash size={24} />}
          </button> */}
          
          {/* Microphone Toggle Button */}
          {/* <button
            onClick={toggleMicrophone}
            disabled={!isJoined}
            className={`p-4 rounded-full ${isMicOn ? 'bg-blue-500 hover:bg-blue-600' : 'bg-red-500 hover:bg-red-600'} text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
            aria-label={isMicOn ? 'Turn off microphone' : 'Turn on microphone'}
          >
            {isMicOn ? <FaMicrophone size={24} /> : <FaMicrophoneSlash size={24} />}
          </button> */}
          
          {/* Settings Button - Shows/Hides Device Selectors */}
          <button
            onClick={() => document.getElementById('device-settings').classList.toggle('hidden')}
            disabled={!isJoined}
            className="p-4 rounded-full bg-gray-700 hover:bg-gray-600 text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Device settings"
          >
            <FaCog size={24} />
          </button>
          
          {/* Leave Button */}
          {/* <button
            onClick={leave}
            disabled={!isJoined}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Leave
          </button> */}
        </div>
        
        {/* Device Settings Panel - Hidden by Default */}
        <div id="device-settings" className="hidden p-4 bg-gray-800 rounded-lg shadow-lg mb-2 transition-all duration-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">Camera</label>
              <select
                value={selectedCamera}
                onChange={handleCameraChange}
                disabled={!isJoined}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="" disabled>Select a camera</option>
                {devices.cameras.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="hidden md:block">
              <label className="block text-white text-sm font-medium mb-2">Microphone</label>
              <select
                value={selectedMic}
                onChange={handleMicChange}
                disabled={!isJoined}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="" disabled>Select a microphone</option>
                {devices.mics.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Video container component
const VideoContainer = ({ participantId, videoRef, audioRef, isLocal, isCameraOn, isCameraSwitching }) => {
  if (!isLocal) return null; // Only show local video
  
  useEffect(() => {
    // Clean up on unmount
    return () => {
      if (videoRef.current[participantId]) {
        videoRef.current[participantId].srcObject = null;
      }
      if (audioRef.current[participantId]) {
        audioRef.current[participantId].srcObject = null;
      }
    };
  }, [participantId, videoRef, audioRef]);

  return (
    <div className="absolute inset-0 bg-black overflow-hidden">
      {/* Camera switching loader overlay */}
      {isCameraSwitching && (
        <div className="absolute inset-0 bg-black flex flex-col items-center justify-center z-30">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mb-4"></div>
          <p className="text-white text-lg font-medium">Loading...</p>
        </div>
      )}
      
      {/* Video element - Full screen */}
      <video
        ref={el => {
          if (el) videoRef.current[participantId] = el;
        }}
        className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1]" /* Apply horizontal flip to fix mirrored image */
        autoPlay
        playsInline
        muted={isLocal} // Mute local video to prevent feedback
        style={{ display: isCameraSwitching ? 'none' : 'block' }}
      />
      
      {/* Audio element */}
      {!isLocal && (
        <audio
          ref={el => {
            if (el) audioRef.current[participantId] = el;
          }}
          autoPlay
        />
      )}
      
      {/* Camera status indicator */}
      <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm z-10 flex items-center space-x-2">
        <div className={`w-2.5 h-2.5 rounded-full ${isLocal && isCameraOn && !isCameraSwitching ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
        <span>{isLocal && isCameraOn && !isCameraSwitching ? 'Camera Active' : 'Camera Off'}</span>
      </div>
    </div>
  );
};

export default DailyCallManager;